import { Maybe } from 'types/UtilityTypes';
import {
  AgentInvocationStatus,
  JudgeVerdict,
  LaneOutcome,
  LaneReasoning,
  QQAgentEngine,
  QQAgentInvocation,
  ReasoningConfidence,
  ReasoningDataSource,
  RuleEvaluation,
  RuleEvaluationStatus,
} from 'types/agents/QuickQuoteAgent';
import { SuggestedQuoteChange } from 'types/suggestions/QuoteSuggestions';

export const isWrittenRulesInvocation = (
  invocation: QQAgentInvocation
): boolean => invocation.engine === QQAgentEngine.LLM;

// Whole dollars, e.g. $2,430.
export const formatDollars = (amount: number): string =>
  `$${Math.round(amount).toLocaleString('en-US')}`;

export const formatPerMile = (amount: number, miles: number): string =>
  `$${(amount / miles).toFixed(2)}/mi`;

export const formatPercent = (value: number): string =>
  `${Number.isInteger(value) ? value : value.toFixed(1)}%`;

export type OutcomeDisplay = {
  label: string;
  className: string;
};

export const OUTCOME_DISPLAY: Record<LaneOutcome, OutcomeDisplay> = {
  [LaneOutcome.Quote]: {
    label: 'Drafted',
    className: 'bg-success-50 text-success-700 border-success-200',
  },
  [LaneOutcome.NoBid]: {
    label: 'No bid',
    className: 'bg-error-50 text-error-700 border-error-200',
  },
  [LaneOutcome.NeedsReview]: {
    label: 'Worth a look',
    className: 'bg-warning-100 text-warning-900 border-warning-300',
  },
  [LaneOutcome.Error]: {
    label: "Couldn't finish",
    className: 'bg-neutral-100 text-neutral-600 border-neutral-300',
  },
};

export const getOutcomeDisplay = (outcome: LaneOutcome): OutcomeDisplay =>
  OUTCOME_DISPLAY[outcome] ?? OUTCOME_DISPLAY[LaneOutcome.Error];

const CONFIDENCE_DISPLAY: Record<ReasoningConfidence, OutcomeDisplay> = {
  [ReasoningConfidence.High]: { label: 'High', className: 'text-success-600' },
  [ReasoningConfidence.Moderate]: {
    label: 'Moderate',
    className: 'text-warning-900',
  },
  [ReasoningConfidence.Low]: { label: 'Low', className: 'text-error-600' },
};

export const getConfidenceDisplay = (
  confidence: LaneReasoning['confidence']
): Maybe<OutcomeDisplay> =>
  confidence ? (CONFIDENCE_DISPLAY[confidence] ?? null) : null;

const SOURCE_LABELS: Record<string, string> = {
  dat: 'DAT',
  // Greenscreens is now Triumph; the data value stays greenscreens.
  greenscreens: 'Triumph',
  tms: 'Your TMS lane history',
  truckstop: 'Truckstop',
};

export const getSourceLabel = (source?: string): string =>
  source ? (SOURCE_LABELS[source.toLowerCase()] ?? source) : '';

export type GroupedEvaluations = {
  applied: RuleEvaluation[];
  couldNotEvaluate: RuleEvaluation[];
  notApplicable: RuleEvaluation[];
};

export const groupRuleEvaluations = (
  lane: LaneReasoning
): GroupedEvaluations => {
  const evaluations = lane.ruleEvaluations ?? [];
  return {
    applied: evaluations.filter(
      (e) => e.status === RuleEvaluationStatus.Applied
    ),
    couldNotEvaluate: evaluations.filter(
      (e) => e.status === RuleEvaluationStatus.CouldNotEvaluate
    ),
    notApplicable: evaluations.filter(
      (e) => e.status === RuleEvaluationStatus.NotApplicable
    ),
  };
};

export const findRule = (
  lane: LaneReasoning,
  ruleId?: string
): Maybe<RuleEvaluation> =>
  ruleId
    ? ((lane.ruleEvaluations ?? []).find((e) => e.ruleId === ruleId) ?? null)
    : null;

export enum JudgeSummaryKind {
  Passed = 'passed',
  Issues = 'issues',
  None = 'none',
}

export type JudgeSummary =
  | { kind: JudgeSummaryKind.Passed; pass: number }
  | { kind: JudgeSummaryKind.Issues; verdict: JudgeVerdict }
  | { kind: JudgeSummaryKind.None };

// The last verdict is the one that decided the outcome.
export const summarizeJudge = (lane: LaneReasoning): JudgeSummary => {
  const verdicts = lane.judge ?? [];
  const last = verdicts[verdicts.length - 1];
  if (!last) {
    return { kind: JudgeSummaryKind.None };
  }
  if (last.passed) {
    return { kind: JudgeSummaryKind.Passed, pass: last.pass };
  }
  return { kind: JudgeSummaryKind.Issues, verdict: last };
};

// Splits text so dollar amounts can be bolded, e.g. "buy at $1,780" -> ["buy at ", "$1,780"].
const DOLLAR_PATTERN = /(\$\d{1,3}(?:,\d{3})*(?:\.\d+)?(?:\/mi)?)/g;

export interface ReasoningTextPart {
  text: string;
  isAmount: boolean;
}

export const splitDollarAmounts = (text: string): ReasoningTextPart[] =>
  text
    .split(DOLLAR_PATTERN)
    .filter((part) => part.length > 0)
    .map((part) => {
      const textPart: ReasoningTextPart = {
        text: part,
        isAmount: /^\$\d/.test(part),
      };
      return textPart;
    });

export const isInvocationInProgress = (invocation: QQAgentInvocation) =>
  invocation.status === AgentInvocationStatus.InProgress;

export const getLanes = (invocation: QQAgentInvocation): LaneReasoning[] =>
  invocation.reasoning?.lanes ?? [];

export const hasDraftedLane = (lanes: LaneReasoning[]): boolean =>
  lanes.some((lane) => lane.outcome === LaneOutcome.Quote);

// "N494809 · Monroe, GA → Chicago, IL" -> { reference: "N494809", lane: "Monroe, GA → Chicago, IL" }.
// Forwarded RXO tables lead with a Movement number; plain labels have no reference.
export interface LaneLabelParts {
  reference: Maybe<string>;
  lane: string;
}

export const splitLaneLabel = (laneLabel: string): LaneLabelParts => {
  const parts = laneLabel.split('·').map((part) => part.trim());
  const laneIndex = parts.findIndex((part) => part.includes('→'));
  if (parts.length < 2 || laneIndex < 0) {
    const plainLabel: LaneLabelParts = {
      reference: null,
      lane: laneLabel.trim(),
    };
    return plainLabel;
  }
  const reference = parts
    .filter((part, index) => index !== laneIndex && part.length > 0)
    .join(' · ');
  const labelParts: LaneLabelParts = {
    reference: reference || null,
    lane: parts[laneIndex],
  };
  return labelParts;
};

const normalizeCity = (value?: string) => (value ?? '').trim().toLowerCase();

// "Monroe, GA → Chicago, IL" -> ["monroe", "chicago"]
const laneCities = (laneLabel: string): Maybe<[string, string]> => {
  const [origin, destination] = splitLaneLabel(laneLabel).lane.split('→');
  if (!origin || !destination) {
    return null;
  }
  return [
    normalizeCity(origin.split(',')[0]),
    normalizeCity(destination.split(',')[0]),
  ];
};

// The pending Quick Quote suggestion for a lane: same quote request first, then same cities.
export const findSuggestionForLane = (
  suggestions: SuggestedQuoteChange[],
  lane?: LaneReasoning
): Maybe<SuggestedQuoteChange> => {
  if (suggestions.length === 0) {
    return null;
  }
  if (!lane) {
    return suggestions.length === 1 ? suggestions[0] : null;
  }

  const { quoteRequestId } = lane;
  if (quoteRequestId) {
    const byId = suggestions.find(
      (s) =>
        s.id === quoteRequestId ||
        s.drumkitQuoteNumber === `DKQN${quoteRequestId}`
    );
    if (byId) {
      return byId;
    }
  }

  const cities = laneCities(lane.laneLabel);
  if (cities) {
    const byCities = suggestions.find(
      (s) =>
        normalizeCity(s.suggested?.pickupCity) === cities[0] &&
        normalizeCity(s.suggested?.deliveryCity) === cities[1]
    );
    if (byCities) {
      return byCities;
    }
  }

  return suggestions.length === 1 ? suggestions[0] : null;
};

// Data sources for a lane: the richer dataSources when present, else the older sources list.
export const getDataSources = (lane: LaneReasoning): ReasoningDataSource[] => {
  if (lane.dataSources && lane.dataSources.length > 0) {
    return lane.dataSources;
  }
  return (lane.sources ?? []).map((source) => {
    const dataSource: ReasoningDataSource = {
      kind: source.source,
      label: source.label,
      rate: source.rate,
      low: source.low,
      high: source.high,
      confidence: source.confidence,
      usedAsBase: source.source === lane.baseSource,
    };
    return dataSource;
  });
};

// Display name for a data source; Greenscreens shows as Triumph (its current brand).
export const getDataSourceLabel = (source: ReasoningDataSource): string => {
  const isGreenscreens =
    String(source.kind).toLowerCase() === 'greenscreens' ||
    /greenscreens/i.test(source.label);
  if (isGreenscreens) {
    return source.label.replace(/greenscreens/gi, 'Triumph') || 'Triumph';
  }
  return source.label;
};

// "$2,111 · $1,950–$2,280 · 84%" unless the backend sent its own summary.
export const summarizeDataSource = (source: ReasoningDataSource): string => {
  if (source.summary) {
    return source.summary;
  }
  const parts: string[] = [];
  if (source.rate !== undefined) {
    parts.push(formatDollars(source.rate));
  }
  if (source.low !== undefined && source.high !== undefined) {
    parts.push(`${formatDollars(source.low)}–${formatDollars(source.high)}`);
  }
  if (source.confidence !== undefined) {
    parts.push(`${Math.round(source.confidence)}%`);
  }
  if (source.recentLoads && source.recentLoads.length > 0) {
    parts.push(`${source.recentLoads.length} loads`);
  }
  return parts.join(' · ');
};
